package org.gstechnovos.jexonplastech.sections.about.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.gstechnovos.jexonplastech.sections.about.styles.AboutHeroContainerStyle
import org.gstechnovos.jexonplastech.theme.toSitePalette
import org.gstechnovos.jexonplastech.util.LocalBreakpoint
import org.gstechnovos.jexonplastech.util.Res
import org.gstechnovos.jexonplastech.util.animateSlideUp
import org.gstechnovos.jexonplastech.util.rememberScrollVisibility
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

// ==========================================
// CMS DATA MODEL
// ==========================================
/**
 *
 * Text:
 * Container Width: The text block is restricted to a maximum width of 800px.
 *
 * Headline: Renders at a massive 50px on desktop and 36px on mobile.
 * Limit to 3–5 words (e.g., "About Jexon Plastech") to avoid taking up too much vertical space.
 *
 * Subheadline: Renders at 20px on desktop and 16px on mobile.
 * Limit to 2–3 lines (approx. 15–25 words).
 *
 */
data class AboutHeroData(
    val headline: String,
    val subheadline: String
)

val defaultAboutHeroData = AboutHeroData(
    headline = "About Jexon Plastech",
    subheadline = "Delivering durable, high-quality homeware solutions and precision manufacturing to global partners since our founding."
)

// ==========================================
// COMPONENT
// ==========================================

@Composable
fun AboutHeroSection(data: AboutHeroData = defaultAboutHeroData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()

    // Using our trusty scroll visibility hook to trigger the load-in animation
    val (isVisible, visibilityRef) = rememberScrollVisibility(threshold = 0.1)

    Column(
        modifier = AboutHeroContainerStyle.toModifier().padding(
            topBottom = if (isDesktop) 120.px else 80.px, leftRight = 5.percent
        ),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Div(
            attrs = Modifier
                .fillMaxWidth()
                .display(DisplayStyle.Flex)
                .flexDirection(FlexDirection.Column)
                .alignItems(com.varabyte.kobweb.compose.css.AlignItems.Center)
                .toAttrs {
                    ref { element ->
                        visibilityRef(element)
                        onDispose {}
                    }
                }
        ) {
            Column(
                modifier = Modifier.maxWidth(800.px).animateSlideUp(isVisible, delayMs = 100),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                // Main Headline
                SpanText(
                    text = data.headline,
                    modifier = Modifier
                        .color(palette.custom.white)
                        .fontSize(if (isDesktop) 50.px else 36.px)
                        .fontWeight(FontWeight.Bold)
                        .margin(bottom = 20.px)
                        .textAlign(TextAlign.Center)
                )

                // Subheadline
                SpanText(
                    text = data.subheadline,
                    modifier = Modifier
                        .color(palette.custom.white.toRgb().copyf(alpha = 0.85f))
                        .fontSize(if (isDesktop) 20.px else 16.px)
                        .lineHeight(1.6)
                        .textAlign(TextAlign.Center)
                )
            }
        }
    }
}